import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import CursorTooltip from "../cta/CursorTooltip";

interface TableProps {
  columns: string[];
  data: Array<{ [key: string]: any }>;
  actionLink?: string;
  customRenderers?: {
    [key: string]: (value: any, row: any) => React.ReactNode;
  };
  customHeaders?: {
    [key: string]: React.ReactNode;
  };
  columnWidths?: string[];
  isLoading?: boolean;
  emptyDateMessage?: string;
  tooltipField?: string; // Field name to use for tooltip text
  selectable?: boolean; // Enable row selection
  selectedItems?: Set<string>; // Set of selected item IDs
  onSelectionChange?: (selectedIds: Set<string>) => void; // Selection change callback
  selectionKey?: string; // Key field to use for selection (e.g., "AmendmentId")
}

const TableContainer = styled.div`
  border: 1px solid var(--Main-stroke-1, #ebebeb);
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background: #fcfcfc;
  border-radius: 18px 18px 0 0;
  border-bottom: 1px solid var(--Main-stroke-1, #ebebeb);
`;

const TableHeaderCell = styled.th<{ width?: string }>`
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 700;
  text-align: left;
  color: #3c3c3d;
  padding: 20px 28px;
  width: ${(props) => props.width || "auto"};
`;

const TableBody = styled.tbody`
  padding: 28px 28px;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #ebebeb;
`;

const TableCell = styled.td<{ width?: string }>`
  padding: 20px 28px;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 700;
  color: #3c3c3d;
  text-align: left;
  width: ${(props) => props.width || "auto"};
  max-width: 400px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ArrowLink = styled(Link)`
  text-decoration: none;
  color: #3c3c3d;
  font-weight: bold;
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  font-family: "Sharp Sans";
  font-size: 14px;
  color: #3c3c3d;
`;

const CheckboxCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #ff734f;
`;

const Table: React.FC<TableProps> = ({
  columns,
  data,
  actionLink,
  customRenderers,
  customHeaders,
  columnWidths,
  isLoading,
  emptyDateMessage,
  tooltipField,
  selectable = false,
  selectedItems = new Set(),
  onSelectionChange,
  selectionKey = "id",
}) => {
  if (isLoading) {
    return (
      <TableContainer>
        <LoadingMessage>Loading...</LoadingMessage>
      </TableContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <TableContainer>
        {emptyDateMessage ? (
          <LoadingMessage>{emptyDateMessage}</LoadingMessage>
        ) : (
          <LoadingMessage>No data available.</LoadingMessage>
        )}
      </TableContainer>
    );
  }
  var pathLink = "";

  const getPathLink = (actionLink: string, id: string) => {
    console.log("pathLink :", actionLink);
    if (actionLink === "AmendmentId") {
      pathLink = `/file/${id}/amendts/`;
      return pathLink;
    } else if (actionLink === "interactionId") {
      pathLink = `/people/${id}/discusions/`;
      return pathLink;
    } else if (actionLink === "fileId") {
      pathLink = `/file/`;
      return pathLink;
    } else {
      pathLink = "";
    }
    return pathLink;
  };

  const getPageLink = (actionLink: string) => {
    let pathLink = "";
    console.log("actionLink :", actionLink);
    if (actionLink === "signal_id") {
      pathLink = `/amendments`;
      return pathLink;
    } else if (actionLink === "fileId") {
      pathLink = `/overview`;
      console.log("pathLink 2:", pathLink);
      return pathLink;
    } else {
      pathLink = "";
    }
    console.log("pathLink 3:", pathLink);
    return pathLink;
  };

  const getRowId = (actionLink: string) => {
    if (actionLink === "interactionId") {
      return "deputyId";
    } else if (actionLink === "AmendmentId") {
      return "FileId";
    } else {
      return "FileId";
    }
  };

  const handleRowSelection = (itemId: string, isSelected: boolean) => {
    if (!onSelectionChange) return;

    const newSelection = new Set(selectedItems);
    if (isSelected) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onSelectionChange(newSelection);
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (!onSelectionChange) return;

    if (isSelected) {
      const allIds = new Set(data.map((row) => row[selectionKey]));
      onSelectionChange(allIds);
    } else {
      onSelectionChange(new Set());
    }
  };

  const isAllSelected =
    data.length > 0 &&
    data.every((row) => selectedItems.has(row[selectionKey]));
  const isIndeterminate = selectedItems.size > 0 && !isAllSelected;

  return (
    <TableContainer>
      <StyledTable>
        <TableHeader>
          <tr>
            {selectable && (
              <TableHeaderCell width="3%">
                <CheckboxCell>
                  <Checkbox
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </CheckboxCell>
              </TableHeaderCell>
            )}
            {columns.map((column, index) => (
              <TableHeaderCell
                key={column}
                width={columnWidths ? columnWidths[index] : undefined}
              >
                {customHeaders && customHeaders[column]
                  ? customHeaders[column]
                  : column}
              </TableHeaderCell>
            ))}
            {actionLink && (
              <TableHeaderCell
                width={columnWidths ? columnWidths[columns.length] : undefined}
              ></TableHeaderCell>
            )}
          </tr>
        </TableHeader>
        <TableBody>
          {data.map((row, index) => {
            const tooltipText =
              tooltipField && row[tooltipField] ? row[tooltipField] : undefined;
            const itemId = row[selectionKey];
            const isSelected = selectedItems.has(itemId);

            const RowComponent = (
              <TableRow key={index}>
                {selectable && (
                  <TableCell width="3%">
                    <CheckboxCell>
                      <Checkbox
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          handleRowSelection(itemId, e.target.checked)
                        }
                      />
                    </CheckboxCell>
                  </TableCell>
                )}
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={column}
                    width={columnWidths ? columnWidths[columnIndex] : undefined}
                  >
                    {customRenderers && customRenderers[column]
                      ? customRenderers[column](row[column], row)
                      : row[column]}
                  </TableCell>
                ))}
                {actionLink && (
                  <TableCell
                    width={
                      columnWidths ? columnWidths[columns.length] : undefined
                    }
                  >
                    <ArrowLink
                      to={`${getPathLink(
                        actionLink,
                        row[getRowId(actionLink)]
                      )}${row[actionLink]}${getPageLink(actionLink)}`}
                    >
                      →
                    </ArrowLink>
                  </TableCell>
                )}
              </TableRow>
            );

            return tooltipText ? (
              <CursorTooltip key={index} text={tooltipText}>
                {RowComponent}
              </CursorTooltip>
            ) : (
              RowComponent
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default Table;
